import {
  Button,
  ButtonGroup,
  Divider,
  HStack,
  Icon,
  Spinner,
  StackDivider,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Sun } from "lucide-react";
import * as R from "ramda";
import { Suspense, useMemo, useState } from "react";
import { FiGrid, FiUsers } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { MFApp } from "state/members/apps";
import { MFMember } from "state/members/member";
import MFMembers from "state/members/membersState";
import { MFOffboardingAccount } from "state/members/offboarding";
import AppGroupView from "./AppGroup";
import HelpModal from "./HelpModal";
import MemberGroupView from "./MemberGroup";

const MemberOffboardings = () => {
  const { isOpen, onToggle } = useDisclosure();
  return (
    <VStack pb="200px" w="full" minH="100vh" spacing={6}>
      <HStack w="full" align="flex-start" spacing={4}>
        <VStack align="flex-start" spacing={1}>
          <HStack spacing={4}>
            <Text variant="lg.sb">Offboarding</Text>
          </HStack>
          <Text fontSize="sm" color="gray.600">
            Below you will see all the offboarding tasks that you've been
            assigned by your IT or security team.{" "}
            <b onClick={onToggle} style={{ cursor: "pointer" }}>
              What is this?
            </b>
          </Text>
        </VStack>
      </HStack>
      <Divider />
      <VStack shadow="xs" w="full" maxW={"full"} spacing={8}>
        <Suspense fallback={<Spinner />}>
          <List />
        </Suspense>
      </VStack>
      <HelpModal isOpen={isOpen} onClose={onToggle} />
    </VStack>
  );
};

const List = () => {
  const [searchParams] = useSearchParams();
  const accounts = MFMembers.useListOffboardings();
  const [view, setView] = useState<"app" | "member">("member");
  const [selectedId, setSelectedId] = useState<string | undefined>(
    searchParams.get("select_offboarding") ?? undefined,
  );

  const [selectedAppId, setSelectedAppId] = useState<string | undefined>(
    searchParams.get("select_app") ?? undefined,
  );
  const remaining = useMemo(
    () => accounts.filter((r) => r.status === "incomplete").length,
    [accounts],
  );

  const byMember: { member: MFMember; accounts: MFOffboardingAccount[] }[] =
    useMemo(() => {
      return Object.entries(
        R.groupBy(
          (a: MFOffboardingAccount) => a.memberAccount.member?.id || "a",
          accounts,
        ),
      ).map(([memId, accounts]) => ({
        member: accounts?.find((a) => a.memberAccount.member?.id === memId)
          ?.memberAccount.member as MFMember,
        accounts: accounts as MFOffboardingAccount[],
      }));
    }, [accounts]);

  const byApp: { app: MFApp; accounts: MFOffboardingAccount[] }[] =
    useMemo(() => {
      return R.sortBy(
        ({ app }) => (app?.name ?? app?.domain ?? "a").toLowerCase(),
        Object.entries(
          R.groupBy(
            (a: MFOffboardingAccount) => a.memberAccount.app?.id || "a",
            accounts,
          ),
        ).map(([appId, accounts]) => ({
          app: accounts?.find((a) => a.memberAccount.appId === appId)
            ?.memberAccount.app as MFApp,
          accounts: accounts as MFOffboardingAccount[],
        })),
      );
    }, [accounts]);

  if (accounts.length === 0) {
    return (
      <VStack pt={4} minH="100vh">
        <Icon as={Sun} boxSize={"56px"} color="gray.500" />
        <Text fontSize="xl" fontWeight="500" color="gray.600">
          Nothing left to do
        </Text>
        <Text fontSize="md" color="gray.500">
          You're all caught up on your offboardings
        </Text>
      </VStack>
    );
  }

  return (
    <VStack w="full" align="flex-end" spacing={4} divider={<StackDivider />}>
      <HStack w="full" justify={"space-between"}>
        {!selectedAppId && !selectedId && (
          <ButtonGroup isAttached size="xs">
            <Button
              leftIcon={<Icon as={FiUsers} />}
              variant={view === "member" ? "primary" : "outline"}
              onClick={() => setView("member")}
            >
              By Employee
            </Button>
            <Button
              variant={view === "app" ? "primary" : "outline"}
              leftIcon={<Icon as={FiGrid} />}
              onClick={() => setView("app")}
            >
              By App
            </Button>
          </ButtonGroup>
        )}
        <Tag size="sm" colorScheme={remaining < 1 ? "green" : "blue"}>
          <TagLabel>
            {remaining > 0 ? `${remaining} accounts remaining` : "All Complete"}
          </TagLabel>
        </Tag>
      </HStack>
      {view === "member"
        ? (selectedId
            ? byMember.filter(
                (o) => o.accounts?.[0]?.offboardingId === selectedId,
              )
            : byMember
          ).map((o) => (
            <MemberGroupView
              isSelected={Boolean(selectedId)}
              onSelect={setSelectedId}
              key={o.member?.id}
              member={o.member}
              accounts={o.accounts}
            />
          ))
        : (selectedAppId
            ? byApp.filter(
                (o) => o.accounts?.[0]?.memberAccount?.appId === selectedAppId,
              )
            : byApp
          ).map((o) => (
            <AppGroupView
              key={o.app?.id}
              isSelected={Boolean(selectedAppId)}
              onSelect={setSelectedAppId}
              app={o.app}
              accounts={o.accounts}
            />
          ))}
    </VStack>
  );
};

export default MemberOffboardings;
